// The signed-in layout: sidebar, the page, the detail panel and the source-document viewer, which
// replaces the page column while open. Keyboard: / search, j/k move through records, Esc close.
import { useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { api } from "../api/client";
import { Face } from "../components/Badge";
import { PasswordDialog } from "../components/PasswordDialog";
import { FeedEditor } from "../pages/FeedEditor";
import { BudgetPanel } from "../pages/BudgetPanel";
import { NoticePanel } from "../pages/NoticePanel";
import { useUI } from "./context";
import { keys, useFeeds, useSession, useSavedSearches, useUnread } from "./queries";

// pdf.js is a third of the bundle; load it the first time a document opens.
const DocumentViewer = lazy(() => import("../components/DocumentViewer").then((m) => ({ default: m.DocumentViewer })));

export function Shell() {
  const me = useSession();
  const { panel, closePanel, viewer, closeViewer, toast, dismissToast, openPanel } = useUI();
  const location = useLocation();
  // A new page closes whatever the panel showed for the old one, except routes that name what to open.
  useEffect(() => {
    if (!/^\/(records|budget)\/./.test(location.pathname)) closePanel();
  }, [location.pathname, closePanel]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (document.querySelector("dialog[open]")) return;
      const target = e.target as HTMLElement;
      const typing = target.matches("input,textarea,select");
      if (e.key === "/" && !typing) {
        e.preventDefault();
        document.getElementById("q")?.focus();
        return;
      }
      if (typing) return;
      if (e.key === "Escape") {
        if (viewer) closeViewer();
        else closePanel();
        return;
      }
      if (e.key === "j" || e.key === "k") {
        const rows = [...document.querySelectorAll<HTMLElement>("#page .row[data-key],#page .pick,#page .result")];
        if (!rows.length) return;
        const at = rows.findIndex((r) => r.getAttribute("aria-selected") === "true");
        const next = rows[e.key === "j" ? Math.min(rows.length - 1, at + 1) : Math.max(0, at - 1)]!;
        openPanel({ kind: "notice", key: next.dataset.key!, feedId: next.dataset.feed ? Number(next.dataset.feed) : null });
        next.scrollIntoView({ block: "nearest" });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [viewer, closeViewer, closePanel, openPanel]);

  return (
    <>
      <Sidebar />
      <main id="main" className={viewer ? "viewing" : ""}>
        <div className="scroll" id="scroll">
          <div className="page" id="page">
            {me.tenant || ["/tenants", "/account"].includes(location.pathname) ? <Outlet /> : <NoTenant />}
          </div>
        </div>
        {viewer ? (
          <Suspense fallback={<section className="viewer" id="viewer"><p className="v-status">Loading the viewer…</p></section>}>
            <DocumentViewer doc={viewer} onClose={closeViewer} />
          </Suspense>
        ) : null}
        {panel ? (
          <aside className="panel" id="panel" aria-label="Opportunity">
            {panel.kind === "notice" ? <NoticePanel key={panel.key + ":" + panel.feedId} target={panel} /> : <BudgetPanel key={panel.id} target={panel} />}
          </aside>
        ) : null}
      </main>
      <div className={`toast ${toast ? "on" : ""}`} id="toast" role="status">
        {toast ? (
          <>
            <span>{toast.message}</span>
            {toast.undo ? (
              <button
                onClick={() => {
                  dismissToast();
                  toast.undo!().catch(() => undefined);
                }}
              >
                Undo
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}

function NoTenant() {
  const me = useSession();
  return (
    <>
      <h1>No organization yet</h1>
      <p className="lede">
        {me.superuser
          ? "Create a tenant under Operator › Tenants, then switch into it from your name at the bottom left."
          : "Your account is not part of any organization. Ask your admin to add you."}
      </p>
    </>
  );
}

function Sidebar() {
  const me = useSession();
  const hasTenant = !!me.tenant;
  const feeds = useFeeds(hasTenant).data;
  const saved = useSavedSearches(hasTenant).data ?? [];
  const unread = useUnread(hasTenant).data ?? 0;
  const [menu, setMenu] = useState(false);
  const [password, setPassword] = useState(false);
  const [newFeed, setNewFeed] = useState(false);
  const navigate = useNavigate();
  const client = useQueryClient();
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("#menu,#me")) setMenu(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menu]);
  const switchTenant = async (id: number) => {
    await api("/api/tenant", { method: "POST", body: { tenant_id: id } });
    setMenu(false);
    await client.resetQueries();
    navigate("/brief");
  };
  const logout = async () => {
    await api("/api/logout", { method: "POST" });
    // Signed out first, so the router swaps in sign-in and unmounts every page; then drop their data.
    // (Clearing the whole cache would detach the session query from the router's observer.)
    client.setQueryData(keys.me, null);
    client.removeQueries({ predicate: (q) => q.queryKey[0] !== keys.me[0] });
    navigate("/");
  };
  const since = feeds?.since;
  let group = "";
  const item = (to: string, label: string, extra?: ReactNode, className = "") => (
    <NavLink key={to} to={to} end={to === "/brief"} className={`item ${className}`.trim()}>
      <span className="label">{label}</span>
      {extra}
    </NavLink>
  );
  return (
    <aside className="side" id="side">
      <Link className="brand" to="/brief">
        <img src="/brand/govchew-logo-dark.svg" height={36} alt="GovChew" />
      </Link>
      <nav className="nav" id="nav" aria-label="Feeds">
        {item("/brief", "Daily brief")}
        {item("/search", "Search")}
        {item("/events", "Events")}
        {item("/budget", "Budget")}
        {item(
          "/alerts",
          "Alerts",
          unread ? (
            <span className="badge-n" aria-label={`${unread} unread`}>
              {unread > 99 ? "99+" : unread}
            </span>
          ) : null,
        )}
        {(feeds?.feeds ?? []).map((f) => {
          const header = f.group !== group ? <h2 key={"g" + f.group}>{f.group}</h2> : null;
          group = f.group;
          return [
            header,
            item(
              `/feeds/${f.id}`,
              f.name,
              <>
                {f.mine ? null : <span className="who">{f.owner_name.split(" ")[0]}</span>}
                <span className="n" title={`${f.fresh} new since ${since ?? "your last visit"}`}>
                  {f.fresh || ""}
                </span>
              </>,
            ),
          ];
        })}
        {!(feeds?.feeds ?? []).some((f) => f.group === "Your feeds") && hasTenant ? <h2>Your feeds</h2> : null}
        {hasTenant ? (
          <button className="item" id="new-feed" onClick={() => setNewFeed(true)}>
            <span className="label">New feed…</span>
          </button>
        ) : null}
        {saved.length ? <h2>Saved searches</h2> : null}
        {saved.map((s) =>
          item(
            `/saved/${s.id}`,
            s.name,
            s.frequency !== "off" ? (
              <span className="n" title="Alerts on">
                🔔
              </span>
            ) : null,
          ),
        )}
        <h2>Organization</h2>
        {item("/members", "Profile and people", null, "admin")}
        {me.superuser ? (
          <>
            <h2>Operator</h2>
            {item("/tenants", "Tenants", null, "admin")}
            <a className="item admin" href="/workbench/">
              <span className="label">Sources and collection</span>
            </a>
          </>
        ) : null}
      </nav>
      <button className="me" id="me" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
        <Face id={me.user.id} text={me.user.initials} />
        <div>
          <b>{me.user.name}</b>
          <span>
            {me.tenant?.name ?? "No tenant"} · {me.superuser ? "Superuser" : me.role === "admin" ? "Admin" : "Member"}
          </span>
        </div>
      </button>
      {menu ? (
        <div className="menu" id="menu" role="menu">
          {me.tenants.length > 1 ? (
            <>
              <h5>Switch organization</h5>
              {me.tenants.map((t) => (
                <button key={t.id} role="menuitem" aria-current={t.id === me.tenant?.id} onClick={() => switchTenant(t.id)}>
                  {t.name}
                </button>
              ))}
              <hr />
            </>
          ) : null}
          <button role="menuitem" id="m-account" onClick={() => (setMenu(false), navigate("/account"))}>
            Account
          </button>
          <button role="menuitem" id="m-password" onClick={() => (setMenu(false), setPassword(true))}>
            Change password
          </button>
          <button role="menuitem" id="m-logout" onClick={logout}>
            Sign out
          </button>
        </div>
      ) : null}
      <PasswordDialog open={password} onClose={() => setPassword(false)} />
      <FeedEditor feed={null} open={newFeed} onClose={() => setNewFeed(false)} />
    </aside>
  );
}
